import { useMemo } from 'react';

import { useEventTime } from '@/components/EventTime';
import { EventStatus } from '@/feature/bets-sbk/hooks/types';
import { Sport } from '@/feature/event-details-sbk/types';
import { useEventDetailsCache } from '@/hooks/use-sbk-event-details-cache';
import { getSportKey } from '@/utils/get-sport-key';
import {
    ScoreboardSport,
    ScoreboardSurface,
    TeamSide,
    getScoreboardVariants,
    getTeamOrder,
    isScoreboardSport,
} from '@/utils/sport-utils';
import { useShallow } from 'zustand/react/shallow';

import { buildBaseballScoreboardProps } from './sports/baseball';
import { buildPeriodScoreboardProps } from './sports/period-scores';
import { buildTennisScoreboardProps } from './sports/tennis';
import {
    BuildProps,
    EventState,
    ScoreBoardGridProps,
    ScoreboardEvent,
    ScoreboardTeamOverride,
    StoreCacheSlice,
    TeamInfo,
} from './types';

// Keyed by `ScoreboardSport` — see SCOREBOARD_SPORTS in sport-utils.
const SPORT_BUILDERS: Record<ScoreboardSport, BuildProps> = {
    tennis: buildTennisScoreboardProps,
    baseball: buildBaseballScoreboardProps,
    american_football: buildPeriodScoreboardProps,
    basketball: buildPeriodScoreboardProps,
    ice_hockey: buildPeriodScoreboardProps,
    soccer: buildPeriodScoreboardProps,
};

export const getEventState = (status: EventStatus | undefined): EventState => {
    const normalized = status?.toUpperCase() ?? '';
    return normalized === 'FINISHED' ? 'final' : normalized === 'LIVE' ? 'live' : 'pregame';
};

/**
 * Whether a sport can render a ScoreBoardGrid. Callers use this to gate the scoreboard/carousel
 * before mounting it, so a sport with period data but no builder (e.g. esports/CS2) never renders an
 * empty slide.
 */
export const sportSupportsScoreboard = (sport: Sport): boolean => {
    const sportKey = getSportKey(sport);
    return !!sportKey && isScoreboardSport(sportKey);
};

export const useScoreboard = (
    event: ScoreboardEvent,
    surface: ScoreboardSurface,
    teamOverride?: ScoreboardTeamOverride
): ScoreBoardGridProps | undefined => {
    const { variants, builder } = useMemo(() => {
        const sportKey = getSportKey(event.sport);
        return {
            variants: getScoreboardVariants(event.sport.name),
            builder: sportKey && isScoreboardSport(sportKey) ? SPORT_BUILDERS[sportKey] : undefined,
        };
    }, [event.sport]);

    const cacheSlice = useEventDetailsCache(
        useShallow(state => {
            const cached = state.eventDetails[event.id];
            return {
                periodScores: cached?.period_scores ?? event.event_details?.period_scores,
                sportSpecific: cached?.sport_specific ?? event.event_details?.sport_specific,
                periodName: cached?.period_name ?? event.event_details?.period_name,
                gameClock: cached?.game_clock ?? event.event_details?.game_clock,
                homeScore: cached?.home_score ?? event.event_details?.home_score ?? 0,
                awayScore: cached?.away_score ?? event.event_details?.away_score ?? 0,
                eventState: getEventState(cached?.status ?? event.status),
            } satisfies StoreCacheSlice;
        })
    );

    const matchDetails = useEventTime(event);

    return useMemo(() => {
        if (!variants || !builder) return undefined;
        const baseProps = builder(event, cacheSlice);
        if (!baseProps) return undefined;

        const [firstSide, secondSide] = getTeamOrder(event.sport.name);
        const withOverride = (team: TeamInfo, side: TeamSide): TeamInfo => {
            if (!teamOverride) {
                return team;
            }
            const overrideIcon = teamOverride.icons?.[side];
            return {
                ...team,
                logoVariant: teamOverride.logoVariant,
                icon: overrideIcon || team.icon,
                shouldShowFallbackLogo: overrideIcon ? false : team.shouldShowFallbackLogo,
            };
        };

        const team1 = withOverride(baseProps.team1, firstSide);
        const team2 = withOverride(baseProps.team2, secondSide);
        return {
            ...baseProps,
            team1,
            team2,
            matchDetails,
            variant: variants[surface],
        };
    }, [event, variants, builder, surface, cacheSlice, matchDetails, teamOverride]);
};
